import { useEffect, useMemo, useRef } from 'react';
import { useSettings } from '@wu/widget-core';
import type { PollSettings, VotesData } from '../../types';

const TOTAL_VOTES = 1000;
const FIRST_LAST_ANSWER_PROPORTION = { first: 0.7, last: 0.3 };

export function useFakeInitialData(answers: PollSettings['answers']) {
  const { createMode, demoMode, template } = useSettings();

  const maxAnswersIdsLength = useRef(answers.length);
  const answersIds = useRef(answers.map(({ id }) => id));

  const creatingProcess = createMode || demoMode;

  const areAnswersFilled = answersIds.current.length > 0 && answers.length > 0;
  const isNotAnswersLengthChanged =
    maxAnswersIdsLength.current === answersIds.current.length;
  const areSameAnswers =
    answers.length === answersIds.current.length &&
    answers.every(({ id }) => answersIds.current.includes(id));

  const areAnswersEqual = isNotAnswersLengthChanged && areSameAnswers;

  const applyFakeData = useMemo(() => {
    if (!areAnswersFilled || !template) {
      return true;
    }

    return creatingProcess ? areAnswersEqual : false;
  }, [template, creatingProcess, areAnswersFilled, areAnswersEqual]);

  const fakeInitialData = setInitialData(answers);

  useEffect(() => {
    if (!creatingProcess) {
      return;
    }

    if (maxAnswersIdsLength.current < answers.length) {
      maxAnswersIdsLength.current = answers.length;
    }

    if (answersIds.current.length === 0 && answers.length > 0) {
      answersIds.current = answers.map(({ id }) => id);
    }
  }, [creatingProcess, answers.length]);

  return { applyFakeData, fakeInitialData };
}

function setInitialData(answers: PollSettings['answers']): VotesData {
  if (!answers || answers.length === 0) {
    return {
      total: TOTAL_VOTES,
      votes: []
    };
  }

  if (answers.length === 1) {
    return {
      total: TOTAL_VOTES,
      votes: [
        {
          id: answers[0].id,
          count: TOTAL_VOTES,
          percentage: 100
        }
      ]
    };
  }

  const votes = [];

  const halfVotes = TOTAL_VOTES / 2;
  const firstAnswerVotes = Math.round(
    halfVotes * FIRST_LAST_ANSWER_PROPORTION.first
  );
  const lastAnswerVotes = Math.round(
    halfVotes * FIRST_LAST_ANSWER_PROPORTION.last
  );

  votes.push({
    id: answers[0].id,
    count: firstAnswerVotes,
    percentage: Math.round((firstAnswerVotes / TOTAL_VOTES) * 100)
  });

  if (answers.length > 2) {
    const remainingVotes = TOTAL_VOTES - (firstAnswerVotes + lastAnswerVotes);
    const middleAnswersCount = answers.length - 2;
    const middleAnswerVotes = Math.floor(remainingVotes / middleAnswersCount);
    const extraVotes = remainingVotes % middleAnswersCount;

    for (let i = 1; i < answers.length - 1; i++) {
      let count = middleAnswerVotes;

      if (i === 1) {
        count += extraVotes;
      }

      votes.push({
        id: answers[i].id,
        count: count,
        percentage: Math.round((count / TOTAL_VOTES) * 100)
      });
    }
  }

  votes.push({
    id: answers[answers.length - 1].id,
    count: lastAnswerVotes,
    percentage: Math.round((lastAnswerVotes / TOTAL_VOTES) * 100)
  });

  return {
    total: TOTAL_VOTES,
    votes
  };
}
